/**
 * `redactUrlForLog()` — a única forma de uma URL chegar ao `console`.
 *
 * Os quatro logs de `api-factory.ts` (`[API SLOW]`, `[API RETRY]` e os dois
 * `[API FAIL]`) imprimem a URL do request SSR **inteira**. Hoje isso já despeja
 * no stdout do container tudo o que o painel manda por query string — e stdout
 * de container não é lugar seguro: fica em disco, entra no log viewer do
 * Coolify e sobrevive ao request que o gerou.
 *
 * Na Onda 4 do plano `api/plans/sso-troca-de-token/` a MESMA linha imprimiria o
 * token de troca, que **é** uma sessão: quem lê o log entra como o operador. O
 * fluxo foi desenhado com POST binding justamente para o token nunca passar por
 * URL (README §3, passo 5→6, que cita estes quatro logs pelo número da linha);
 * este arquivo é a segunda linha de defesa, para o dia em que alguém
 * "simplificar" o hop e voltar a passar o token por GET.
 *
 * ## Por que ALLOWLIST, e não denylist
 *
 * Uma denylist (`token`, `code`, `secret`, `senha`…) só protege o que já
 * conhecemos, e falha **ABERTA**: o parâmetro que alguém acrescentar daqui a
 * seis meses — `otp`, `convite`, `sso_state` — nasce fora da lista e vaza em
 * silêncio, sem nenhum teste ficar vermelho para avisar. A allowlist falha
 * **FECHADA**: o parâmetro novo nasce redigido; no pior caso o log fica menos
 * legível, e quem quiser o valor de volta precisa escrever o nome aqui — num
 * diff que passa por review.
 *
 * É a mesma escolha de `api/src/lib/trusted-origins.ts`, que recusa curinga no
 * boot em vez de avisar: neste repo, default-allow já custou caro.
 *
 * O **nome** do parâmetro sempre sobrevive: `?token=[REDIGIDO]` continua dizendo
 * que havia um token ali, que é o que o diagnóstico precisa saber. Some o valor,
 * não a forma da URL.
 *
 * ## Regra de admissão à allowlist
 *
 * Só entra parâmetro cujo valor seja, **por construção**, número, enum de
 * ordenação ou idioma — nunca algo que possa carregar identificador, conteúdo
 * digitado pelo usuário ou credencial. Foi por essa régua que `from`/`to` de
 * relatório ficaram **de fora** mesmo aparecendo nas chamadas de hoje: hoje são
 * datas, mas o nome comporta um endereço de e-mail sem que ninguém precise
 * renomear nada. `q`/`search` idem — é o que o usuário digitou.
 *
 * ⚠️ Nada aqui lança. Um log que derruba o request transformaria lentidão
 * (o caso do `[API SLOW]`) em indisponibilidade.
 */

/** O marcador. Constante para o teste e o `grep` casarem com uma coisa só. */
const REDACTED = '[REDIGIDO]'

/**
 * Devolvido quando a redação em si falha — não deveria acontecer (a função é
 * fatiamento de string), mas um log mudo é preferível a um throw. O texto é
 * propositalmente greppável: se aparecer em produção, é bug DESTE arquivo.
 */
const UNPARSEABLE_URL = '[URL NÃO ANALISÁVEL]'

/**
 * Nomes cujo valor pode aparecer no log, já normalizados (minúsculos).
 *
 * Medido nas chamadas Eden dos apps de operador (`grep "query: {"` em
 * `v2|assist|hub|creator/src`, 2026-08): `period` (10 ocorrências), `limit` (5)
 * e `pageSize` (4) são os que realmente explicam latência num `[API SLOW]` —
 * é para isso que a lista existe. O resto do universo medido (`documentId`,
 * `rootId`, `cep`, `anchor`, `location_code`…) é identificador ou dado do
 * cliente, e fica de fora de propósito.
 */
const READABLE_PARAMS = new Set([
  'page',
  'pagesize',
  'per_page',
  'perpage',
  'limit',
  'offset',
  'skip',
  'take',
  'sort',
  'sortby',
  'order',
  'orderby',
  'direction',
  'dir',
  'locale',
  'lang',
  'language',
  'period',
])

/**
 * Teto para o valor de um parâmetro da allowlist.
 *
 * `?sort=` legítimo tem uma dúzia de caracteres. O teto fecha o caso "nome
 * inocente, valor arbitrário" — quem quiser esconder um segredo dentro de
 * `sort` precisa que ele caiba em 64 caracteres, e um token de sessão do
 * `better-auth` (32 bytes, base64url) não cabe junto com mais nada.
 */
const READABLE_VALUE_MAX_LENGTH = 64

/**
 * Normaliza o nome do parâmetro só para a COMPARAÇÃO com a allowlist.
 *
 * Percent-encoding no nome é legal (`%74oken` é `token`) e seria um bypass
 * trivial de qualquer comparação crua. Decodificação malformada (`%E0%A4`)
 * **não** lança: cai em `null`, e `null` nunca casa com a allowlist — falha
 * fechada, como o resto do arquivo.
 */
function normalizeParamName(rawName: string): string | null {
  try {
    return decodeURIComponent(rawName.replace(/\+/g, ' ')).trim().toLowerCase()
  } catch {
    return null
  }
}

/** Decide um par `nome=valor` da query. */
function redactQuerySegment(segment: string): string {
  // `?a=1&&b=2` e `?&a=1` produzem segmento vazio: não há valor para esconder, e
  // reescrevê-lo mudaria a URL do log sem motivo.
  if (segment === '') return ''

  const equalsIndex = segment.indexOf('=')

  // Segmento sem `=`: o segmento INTEIRO é o único texto que existe ali, então
  // ou ele é um nome conhecido (flag) ou pode ser um valor solto. Sem `=` não dá
  // para "preservar o nome e esconder o valor" — some inteiro.
  if (equalsIndex === -1) {
    return READABLE_PARAMS.has(normalizeParamName(segment) ?? '') ? segment : REDACTED
  }

  const rawName = segment.slice(0, equalsIndex)
  const value = segment.slice(equalsIndex + 1)
  const name = normalizeParamName(rawName)

  const readable =
    name !== null && READABLE_PARAMS.has(name) && value.length <= READABLE_VALUE_MAX_LENGTH

  // O nome sai como veio, sem re-encodar: o log é para leitura humana, e
  // normalizar o que não precisa só cria diferença entre o log e o request real.
  return `${rawName}=${readable ? value : REDACTED}`
}

/**
 * Esconde credencial embutida na autoridade (`https://user:senha@host/…`).
 *
 * `INTERNAL_API_URL` vem de env: basic auth ali é uma linha no painel do
 * Coolify, e cairia no log de todo request lento. A autoridade termina na
 * primeira `/` — query e fragmento já foram separados antes de chegar aqui.
 */
function redactAuthority(base: string): string {
  const schemeIndex = base.indexOf('://')
  if (schemeIndex === -1) return base

  const authorityStart = schemeIndex + 3
  const slashIndex = base.indexOf('/', authorityStart)
  const authorityEnd = slashIndex === -1 ? base.length : slashIndex
  const authority = base.slice(authorityStart, authorityEnd)

  // `lastIndexOf`: `user:se@nha@host` é autoridade válida, e o host é o que vem
  // depois da ÚLTIMA arroba.
  const atIndex = authority.lastIndexOf('@')
  if (atIndex === -1) return base

  return `${base.slice(0, authorityStart)}${REDACTED}@${authority.slice(atIndex + 1)}${base.slice(authorityEnd)}`
}

/**
 * A URL como ela pode ir para o `console`.
 *
 * - sem `?`, `#` nem `@` → devolve a MESMA string, sem alocar nada. É o caminho
 *   da maioria dos requests, e isto roda dentro do `fetch` do SSR.
 * - valor de parâmetro fora da allowlist → `[REDIGIDO]`; o nome fica.
 * - fragmento → some inteiro (`#[REDIGIDO]`). Fragmento não é enviado ao
 *   servidor: se apareceu numa URL de `fetch`, não tem função de diagnóstico
 *   nenhuma — e é exatamente onde os fluxos OAuth implícitos devolvem token.
 * - relativa, sem esquema, malformada → tratada como texto; **nunca** lança e
 *   nunca passa por `new URL()`, que rejeitaria a relativa e normalizaria o
 *   resto (o log passaria a divergir da URL realmente chamada).
 */
export function redactUrlForLog(url: string): string {
  try {
    if (!url.includes('?') && !url.includes('#') && !url.includes('@')) return url

    const hashIndex = url.indexOf('#')
    const withoutFragment = hashIndex === -1 ? url : url.slice(0, hashIndex)
    const fragment = hashIndex === -1 ? '' : `#${REDACTED}`

    const queryStart = withoutFragment.indexOf('?')
    if (queryStart === -1) return `${redactAuthority(withoutFragment)}${fragment}`

    const base = redactAuthority(withoutFragment.slice(0, queryStart))
    const query = withoutFragment
      .slice(queryStart + 1)
      .split('&')
      .map(redactQuerySegment)
      .join('&')

    return `${base}?${query}${fragment}`
  } catch {
    return UNPARSEABLE_URL
  }
}
